/**
 * DI-3-C — the intake RUNNER: an app-level module singleton, not a page
 * (story §2.1; locked "the-runner-is-app-level-not-page-level").
 *
 * It owns the index worker(s) (`IndexClient`, DI-2-1 — called, not changed)
 * and the rows they fill (`intakeIndexStore`), and it drives every started
 * data set through
 *
 *   decided → creating → created → syncing → synced → completing → running
 *
 * one request at a time: first every create, in row order (one failure never
 * rolls back another set — the set keeps its error and a Retry), then the
 * round trip of each created set AS SOON AS ITS ROWS ARE HASHED (the hash is
 * the only gate, and only on `syncing`; `sync/roundTrip.ts` verbatim, batches
 * ≤ 500 / 900 kB), then `metadata-complete` (DI-3-A; N5 — no Match button).
 * Leaving the page does NOT stop it and does NOT dispose the worker: the
 * page subscribes, it does not own. The runner disposes the worker only on
 * "Start over" — in M3a a finished session always still has something
 * pending that needs the files (the "now" upload is DI-3-F's phase, the
 * verify card's previews are read from this tab), so "finished and nothing
 * pending" does not occur yet.
 *
 * Every transition is written to the session record (IndexedDB, the existing
 * `sessions` store): a reload shows where each set stands, and `restore`
 * continues the steps that need no files (a create, `metadata-complete`);
 * the round trip waits for the folder to be read again ("Continue session").
 * `metadata-complete` is posted ONCE per set per session: a repeated Start of
 * the same session (a re-drop, which replays the create and the round trip)
 * posts it again only when the round trip created new points.
 *
 * "Add another folder" appends rows with an index client of its own whose
 * results are shifted by the rows before (a store proxy — the worker client
 * is reused as it is). Sets are regrouped until Start and never after.
 *
 * DI-3-D (N8) — resume from any tab: `reattach` re-reads the folder of a
 * restored session (the resume module, `resume.ts`, re-acquires the handle),
 * gives every started set its members back once the heads are parsed (by the
 * set's stable id), and hands the hashes persisted at the set's round trip
 * (`ClusterIntake.rowHashes`, keyed file name + size) to the index worker
 * (DI-3-P2): a matching file is head-parsed only and reports them, its bytes
 * are not read again — so the round trip continues without a second hash
 * pass and replays idempotently. The board's words for a set (`turbineLabel`
 * / `instructionLabel`), its bytes and the round trip's progress are
 * persisted too. Which TAB runs the runner is `claim.ts`'s business.
 */
import type { StoreApi } from "zustand";
import {
  auxOf,
  createIndexStore,
  intakeIndexStore,
  type IndexPhase,
  type IndexState,
} from "../indexStore.js";
import {
  IndexClient,
  type PreviewAnswer,
  type WorkerLike,
} from "../parse/workerClient.js";
import type { CollectResult } from "../files/dropToFiles.js";
import {
  createAcquisition,
  describeCreateError,
  heldAcquisitionOf,
  type CreateAcquisitionInput,
} from "../turbine/createAcquisition.js";
import { isInspection3FetchError } from "../api/inspection3Fetch.js";
import type { DataAcquisitionCreated } from "../api/types.js";
import {
  postMetadataComplete,
  type PostMetadataComplete,
} from "../api/metadataComplete.js";
import { isIntakeFetchError, reasonOf } from "../api/intakeFetch.js";
import { withdrawIntake } from "../api/withdraw.js";
import { deadlineWords, isRequestTimeout } from "../api/deadline.js";
import {
  RoundTripFailure,
  describeRoundTripError,
  postDataPoints,
  syncAcquisition,
  type PostDataPoints,
} from "../sync/roundTrip.js";
import { syncProgressStore } from "../sync/syncProgressStore.js";
import {
  activeSessionId,
  forgetActiveSession,
  getSession,
  rememberActiveSession,
  saveSession,
  updateCluster,
  updateSession,
  type ClusterIntake,
  type CreatePlan,
  type ImageUploadMode,
  type IntentRecord,
  type SessionRecord,
  type SetDecision,
  type UploadChoice,
} from "../session/sessionStore.js";
import { enter, type SetError, type SetPhase } from "./phases.js";
import {
  ladderAfterFailure,
  ladderOnAttempt,
  retryDue,
  type AutoRetry,
} from "./autoRetry.js";
import {
  effectiveWindow,
  firstCaptureOf,
  folderMembers,
  groupForDecide,
  proposalSets,
  sendPlan,
  type ProposalSet,
} from "./sets.js";
import { recall, remember, rootPathOf } from "../files/handleStore.js";
import { fileKey } from "../add/diff.js";
import type { HashResult } from "../parse/types.js";
import { hashFile } from "../parse/hashes.js";
import { LOOSE_DROP_NAME, rowHashKey } from "../session/sessionStore.js";
import {
  hashRecordOf,
  knownForFolder,
  knownHashesOf,
} from "../parse/hashKey.js";
import { UploadDriver, type DriverDeps } from "./uploadDriver.js";
import { REAL_UPLOAD_API } from "../upload/api.js";
import { auxFilesOf, sendPosSession } from "../upload/posSession.js";
import { onApproved } from "../upload/approvals.js";
import { initialUpload, uploadFinished } from "../upload/state.js";
import {
  createRunnerStore,
  EMPTY_RUNNER,
  hasPendingLocalWork,
  patchSet,
  type ReattachCounts,
  type RunnerSet,
  type RunnerState,
} from "./runnerStore.js";

/** DI-3-S40 R2 — the withdraw reason Start over sends (3–500 characters). */
export const START_OVER_REASON = "start over";

export interface RunnerDeps {
  /** The index store the worker fills (the app's by default). */
  indexStore?: StoreApi<IndexState>;
  createWorker?: () => WorkerLike;
  /** 0 = apply every worker message at once (tests). */
  flushMs?: number;
  create?: (input: CreateAcquisitionInput) => Promise<DataAcquisitionCreated>;
  post?: PostDataPoints;
  complete?: PostMetadataComplete;
  now?: () => Date;
  /**
   * DI-3-F — the upload's seam: the gateway calls, the blob `fetch`, the
   * timers. Absent = the real `inspection-3` / `intake` calls and `fetch`.
   */
  upload?: Partial<DriverDeps>;
  /**
   * DI-3-S16-FE — where a dropped folder sits under the granted field-data
   * root (`root.resolve(handle)`); absent = `handleStore.ts:rootPathOf`.
   */
  rootPathOf?: (handle: unknown) => Promise<string[] | null>;
  /**
   * DI-3-S40 R2 — `POST …/intake/withdraw` for a set Start over abandons;
   * absent = `api/withdraw.ts:withdrawIntake`.
   */
  withdraw?: (daId: string, reason: string) => Promise<unknown>;
  /** DI-3-S68 B2 — the automatic retry's timers (tests: fake ones). */
  timers?: RetryTimers;
  /** DI-3-S68 B4 — a file's hashes, read again on Retry (default `hashFile`). */
  hash?: (file: Blob) => Promise<HashResult>;
  /** DI-3-S68 F6b — does one byte of the file read (the drive back)? */
  readable?: (file: Blob) => Promise<boolean>;
  /** DI-3-S68 B2 — where `online` is heard (default `window`; null = nowhere). */
  win?: Pick<Window, "addEventListener" | "removeEventListener"> | null;
  /** DI-3-S75 — the pos-only session's bound (default `POS_SESSION_TIMEOUT_MS`, 30 s). */
  posTimeoutMs?: number;
}

/** DI-3-S68 B2 — the retry ladder's clock. */
export interface RetryTimers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (id: unknown) => void;
}

const REAL_RETRY_TIMERS: RetryTimers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (id) => {
    globalThis.clearTimeout(id as ReturnType<typeof setTimeout>);
  },
};

/** DI-3-S68 B2 — a refused gate (not the claiming tab) looks again after this. */
export const RETRY_GATE_RECHECK_MS = 5_000;

/** DI-3-S68 B4 — the error reason of a set stopped on files it could not read. */
export const UNREADABLE_REASON = "unreadable_files";

/** DI-3-S68 B4 — "3 files could not be read — reconnect the drive or choose the folder." */
export function unreadableError(n: number, drive?: "gone"): SetError {
  return {
    step: "sync",
    message: `${String(n)} ${n === 1 ? "file" : "files"} could not be read — reconnect the drive or choose the folder.`,
    status: null,
    reason: UNREADABLE_REASON,
    ...(drive ? { drive } : {}),
  };
}

/**
 * DI-3-S68 F6b — while a set waits on files it could not read, this tab
 * looks every `DRIVE_PROBE_MS` whether ONE of them reads again (one byte):
 * the drive reconnected → the set is read again by itself, visibly
 * ("Reading again…"), never a silent jump. Only the tab holding the claim,
 * only while the files are this tab's.
 */
export const DRIVE_PROBE_MS = 10_000;

/** One set as Start hands it to the runner. */
export interface StartSet {
  set: ProposalSet;
  decision: SetDecision;
  plan: CreatePlan;
  /**
   * The set's workflow uploads the originals (`imagesUploaded`) — M5-NODE-01:
   * under `optional` the pilot's choice ("don't upload" → false).
   */
  uploads: boolean;
  /** M5-NODE-01 — the version's served image upload mode (Approve reads it). */
  imageUpload?: ImageUploadMode;
  turbineLabel: string;
  instructionLabel: string;
}

export interface StartInput {
  sessionId: string;
  folderName: string | null;
  intent: IntentRecord;
  uploadChoice: UploadChoice;
  sets: readonly StartSet[];
}

/**
 * DI-3-G — a folder read for "Add images…": an index of its OWN (a second
 * index in the tab — the session's rows, previews and pending uploads are not
 * touched while the pilot looks at the counts). Start adopts it (`addToSet`);
 * a dialog closed without Start disposes it.
 */
export interface AddIndex {
  /** The child session's id (its externalIds are the acquisition's, not this). */
  readonly sessionId: string;
  readonly folderName: string | null;
  readonly store: StoreApi<IndexState>;
  readonly files: readonly Blob[];
  /** The worker client — adopted by the runner at Start, never re-run. */
  readonly client: IndexClient;
  dispose: () => void;
}

/** DI-3-G — "Start" in the Add images dialog: everything the child session needs. */
export interface AddToSetInput {
  add: AddIndex;
  /** The EXISTING acquisition — nothing is created. */
  daId: string;
  /** The set's number on the board ("data set 1"). */
  ordinal: number;
  turbineLabel: string;
  instructionLabel: string;
  /** The set's workflow key, when known (display; the server has it frozen). */
  workflow: string | null;
  instructionId: string | null;
  assetId: string | null;
  siteId: string | null;
  projectId: string | null;
  centroid: { lat: number; lon: number };
  /** The set's workflow uploads the originals (N2 — the radios were shown). */
  uploads: boolean;
  uploadChoice: UploadChoice;
  /** The new files' keys (`add/diff.ts`) — the upload sends only these. */
  newKeys: readonly string[];
  /** The parent set, when this browser started it. */
  parent: { sessionId: string | null; clusterId: string | null } | null;
  /** The picked folder's handle — remembered so a resume can read it again. */
  handle?: unknown;
  /**
   * DI-3-R1-FE-pilot — the rows the child set binds (default: every member of
   * the folder, `folderMembers`). "Images required" binds ONLY the files the
   * set already holds (`upload/fromFolder.ts`), so a folder holding other
   * images never adds them to the set.
   */
  memberIndexes?: readonly number[];
}

/** DI-3-G — why `addToSet` refused (the dialog words it before Start). */
export class AddRefused extends Error {
  constructor(readonly reason: "busy" | "not_indexed") {
    super(
      reason === "busy"
        ? "this tab still works on another drop"
        : "the folder is still being read",
    );
  }
}

interface ClientSlot {
  offset: number;
  count: number;
  client: IndexClient;
  phase: IndexPhase;
  /** DI-3-F — the files of this client's rows (the upload reads their slices). */
  files: readonly Blob[];
}

const INDEX_DONE: readonly IndexPhase[] = ["done", "cancelled", "error"];

function errorOf(
  step: SetError["step"],
  e: unknown,
  underInstruction = false,
): SetError {
  if (step === "create") {
    const held = heldAcquisitionOf(e);
    const status = isInspection3FetchError(e) ? e.status : null;
    const body = (isInspection3FetchError(e) ? e.body : null) as {
      reason?: unknown;
    } | null;
    return {
      step,
      message: describeCreateError(e, underInstruction),
      status,
      reason: typeof body?.reason === "string" ? body.reason : null,
      ...(held ? { heldAcquisitionId: held } : {}),
    };
  }
  if (step === "sync") {
    const d =
      e instanceof RoundTripFailure ? e.detail : describeRoundTripError(e);
    return { step, message: d.message, status: d.status, reason: d.reason };
  }
  return {
    step,
    message: isIntakeFetchError(e)
      ? `The server did not take the hand-over (HTTP ${String(e.status)}${reasonOf(e) ? `, ${reasonOf(e) ?? ""}` : ""}). Retry.`
      : isRequestTimeout(e)
        ? `The server ${deadlineWords(e)}. Retry.`
        : "The server could not be reached. Retry.",
    status: isIntakeFetchError(e) ? e.status : null,
    reason: reasonOf(e),
  };
}

/**
 * A started cluster of a session record as the runner holds it (members
 * empty until the folder is read again); null for a cluster never started.
 * `restore` builds its sets with it; DI-3-S4-FE — so does the upload lane.
 */
/**
 * M5-NODE-01 — a SET's upload choice from the drop's: "don't upload" never
 * reaches a set (the runner writes `null` — the partner path — for a set that
 * does not upload); a set that uploads under it goes "now".
 */
export function setChoiceOf(c: UploadChoice): UploadChoice {
  return c === "none" ? "now" : c;
}

export function runnerSetFromCluster(
  c: ClusterIntake,
  k: number,
): RunnerSet | null {
  const { phase, plan } = c;
  if (!phase || !plan) return null;
  const r: RunnerSet = {
    clusterId: c.clusterId,
    ordinal: c.ordinal ?? k + 1,
    memberIndexes: [],
    imageCount: c.imageCount,
    sizeBytes: c.sizeBytes ?? 0,
    centroid: c.centroid,
    plan,
    turbineLabel: c.turbineLabel ?? "",
    instructionLabel: c.instructionLabel ?? "",
    uploadChoice: c.uploadChoice ?? null,
    phase,
    phaseTimes: c.phaseTimes ?? {},
    error: c.error ?? null,
    ...(c.autoRetry ? { autoRetry: c.autoRetry } : {}),
    ...(c.dataAcquisitionId
      ? {
          created: {
            dataAcquisitionId: c.dataAcquisitionId,
            state: "CREATED",
            createdAt: "",
            externalId: c.externalId ?? null,
            idempotentReplay: false,
            instructionId: c.instructionId ?? null,
            siteId: c.siteId ?? null,
            assetId: c.assetId ?? null,
            intakeState: (c.intakeState ??
              null) as DataAcquisitionCreated["intakeState"],
            workflow: c.workflow ?? null,
          } as DataAcquisitionCreated,
        }
      : {}),
    ...(c.roundTrip ? { roundTrip: c.roundTrip } : {}),
    ...(c.metadataComplete ? { metadataComplete: c.metadataComplete } : {}),
    ...(c.syncProgress ? { syncProgress: c.syncProgress } : {}),
    ...(c.upload ? { upload: c.upload } : {}),
    ...(c.withhold ? { withhold: c.withhold } : {}),
    // DI-3-G — an add: bound to its acquisition, the new files' keys
    ...(c.boundTo ? { boundTo: c.boundTo } : {}),
    ...(c.uploadKeys ? { uploadKeys: c.uploadKeys } : {}),
    // M5-NODE-01 — the mode at Start (Approve's `uploadImages`) and the
    // escape hatch's reason (every session open sends it)
    ...(c.imageUpload ? { imageUpload: c.imageUpload } : {}),
    ...(c.uploadReason ? { uploadReason: c.uploadReason } : {}),
  };
  return r;
}

export class IntakeRunner {
  readonly state: StoreApi<RunnerState> = createRunnerStore();
  readonly index: StoreApi<IndexState>;
  private readonly deps: Required<
    Omit<
      RunnerDeps,
      | "indexStore"
      | "createWorker"
      | "flushMs"
      | "upload"
      | "timers"
      | "win"
      | "posTimeoutMs"
    >
  > &
    Pick<RunnerDeps, "createWorker" | "flushMs">;
  private clients: ClientSlot[] = [];
  private running = false;
  private dirty = false;
  private persistChain: Promise<unknown> = Promise.resolve();
  private unsubscribe: (() => void) | null = null;
  /** DI-3-D — a reattach waits for the heads to give the sets their members. */
  private awaitingMembers = false;
  /** DI-3-F — the originals' upload and the partner sets' withhold. */
  readonly uploads: UploadDriver;
  private offApproved: (() => void) | null = null;
  /** DI-3-P6 — the session `release` is ending (one release per session). */
  private releasing: string | null = null;
  /**
   * DI-3-S40 (B′) — the folders this drop read (the drop, "Add another
   * folder"): "Drop the next folder" hands them to the upload lane, which
   * then needs no gesture and no handle — in any browser, within this tab.
   */
  private dropped: CollectResult[] = [];
  /** DI-3-S68 B2 — one armed timer per set waiting on the ladder. */
  private readonly retryTimers = new Map<string, unknown>();
  private readonly retryClock: RetryTimers;
  private readonly win: Pick<
    Window,
    "addEventListener" | "removeEventListener"
  > | null;
  /** DI-3-S68 B2 — may THIS tab retry by itself? (`resume.ts`: the claim's owner) */
  private retryGate: () => boolean = () => true;
  private readonly onOnline = (): void => {
    this.fireDue("online");
  };
  /** DI-3-S75 — the pos-only session's seam (the upload's api / fetch / timers). */
  private readonly posDeps: Pick<RunnerDeps, "posTimeoutMs"> & {
    upload: Partial<DriverDeps>;
  };

  constructor(deps: RunnerDeps = {}) {
    this.posDeps = {
      upload: deps.upload ?? {},
      ...(deps.posTimeoutMs !== undefined
        ? { posTimeoutMs: deps.posTimeoutMs }
        : {}),
    };
    this.retryClock = deps.timers ?? REAL_RETRY_TIMERS;
    this.win =
      deps.win === undefined
        ? typeof window !== "undefined"
          ? window
          : null
        : deps.win;
    this.win?.addEventListener("online", this.onOnline);
    this.index = deps.indexStore ?? intakeIndexStore;
    this.deps = {
      create: deps.create ?? createAcquisition,
      post: deps.post ?? postDataPoints,
      complete: deps.complete ?? postMetadataComplete,
      now: deps.now ?? (() => new Date()),
      // read at the call (a module double without it never breaks the runner)
      rootPathOf:
        deps.rootPathOf ?? ((h) => Promise.resolve().then(() => rootPathOf(h))),
      withdraw: deps.withdraw ?? withdrawIntake,
      hash: deps.hash ?? hashFile,
      readable:
        deps.readable ??
        ((f: Blob) =>
          f
            .slice(0, 1)
            .arrayBuffer()
            .then(
              () => true,
              () => false,
            )),
      ...(deps.createWorker ? { createWorker: deps.createWorker } : {}),
      ...(deps.flushMs !== undefined ? { flushMs: deps.flushMs } : {}),
    };
    this.uploads = new UploadDriver(
      {
        state: this.state,
        index: this.index,
        fileOf: (i) => this.fileOf(i),
        persist: (fn) => this.persist(fn),
        now: this.deps.now,
      },
      { api: REAL_UPLOAD_API, ...(deps.upload ?? {}) },
    );
    // N2 "after verification": Approve answered approved_pending_upload
    this.offApproved = onApproved((daId, at) =>
      this.uploads.approved(daId, at),
    );
    // a created set waits for its hashes: every index batch may unblock one
    this.unsubscribe = this.index.subscribe((s, prev) => {
      if (
        this.awaitingMembers &&
        s.total > 0 &&
        s.parsedCount + s.errors.length >= s.total &&
        (s.parsedCount !== prev.parsedCount || s.phase !== prev.phase)
      )
        this.giveMembersBack();
      if (s.hashedCount !== prev.hashedCount || s.phase !== prev.phase)
        this.pump();
    });
  }

  // ── the index ─────────────────────────────────────────────────────────

  /** Is a worker alive (tests read it: leaving the page must not dispose it). */
  get hasWorker(): boolean {
    return this.clients.length > 0;
  }

  /** A new drop (or "Continue session" under the same id): index it. */
  drop(sessionId: string, result: CollectResult): void {
    this.disposeClients();
    this.awaitingMembers = false;
    this.state.setState({
      ...EMPTY_RUNNER,
      sessionId,
      folderName: result.folderName,
      indexStartedAt: this.deps.now().toISOString(),
    });
    this.dropped = [result];
    this.index.getState().load({
      sessionId,
      folderName: result.folderName,
      files: result.files.map((f) => ({
        name: f.file.name,
        relativePath: f.relativePath,
        size: f.file.size,
      })),
      skipped: result.skipped,
      aux: auxOf(result),
    });
    this.startClient(sessionId, 0, result);
  }

  /** "Add another folder" (before Start): rows appended, sets regrouped. */
  addFolder(result: CollectResult): void {
    const st = this.index.getState();
    if (!st.sessionId || this.state.getState().started) return;
    const offset = st.rows.length;
    this.dropped.push(result);
    st.append({
      files: result.files.map((f) => ({
        name: f.file.name,
        relativePath: f.relativePath,
        size: f.file.size,
      })),
      skipped: result.skipped,
      aux: auxOf(result),
    });
    this.startClient(st.sessionId, offset, result);
  }

  cancelIndex(): void {
    for (const c of this.clients) c.client.cancel();
  }

  // ── DI-3-G — "Add images…" onto an existing data set ─────────────────

  /**
   * Read a folder for the Add images dialog into an index of its own (the
   * session this tab runs keeps its rows, previews and uploads meanwhile).
   */
  readAddFolder(result: CollectResult, sessionId: string): AddIndex {
    const store = createIndexStore();
    const files = result.files.map((f) => f.file);
    store.getState().load({
      sessionId,
      folderName: result.folderName,
      files: result.files.map((f) => ({
        name: f.file.name,
        relativePath: f.relativePath,
        size: f.file.size,
      })),
      skipped: result.skipped,
      aux: auxOf(result),
    });
    const client = new IndexClient({
      store,
      ...(this.deps.createWorker
        ? { createWorker: this.deps.createWorker }
        : {}),
      ...(this.deps.flushMs !== undefined
        ? { flushMs: this.deps.flushMs }
        : {}),
    });
    if (result.files.length > 0)
      client.start(
        sessionId,
        result.files.map((f) => ({
          file: f.file,
          name: f.file.name,
          size: f.file.size,
          relativePath: f.relativePath,
        })),
      );
    else store.getState().setPhase("done");
    return {
      sessionId,
      folderName: result.folderName,
      store,
      files,
      client,
      dispose: () => {
        // adopted by `addToSet`: the runner owns the worker now
        if (this.clients.some((c) => c.client === client)) return;
        client.dispose();
      },
    };
  }

  /**
   * Start of an add (N13): a CHILD session bound to the existing acquisition
   * — one set, no create, no new instruction. The dialog's index is adopted
   * (the worker is not re-run; every file is parsed and hashed already), the
   * set enters at `created` with `created` = the acquisition, and the queue
   * does the rest unchanged: the round trip over EVERY file of the folder
   * (the present ones replay — the externalId formula is the acquisition's,
   * `dataPointExternalId(daId, hashes)`), `metadata-complete` once, then the
   * upload of the NEW files only (`uploadKeys`). The child session becomes
   * the browser's active session: the board shows it, a reload resumes it.
   *
   * Refused while this tab still has browser work of another drop pending
   * (its files are only here) — the dialog says so before Start.
   */
  async addToSet(input: AddToSetInput): Promise<void> {
    const cur = this.state.getState();
    if (cur.started && hasPendingLocalWork(cur)) throw new AddRefused("busy");
    const src = input.add.store.getState();
    if (src.phase !== "done") throw new AddRefused("not_indexed");
    const sid = input.add.sessionId;
    const now = this.deps.now();
    const at = now.toISOString();

    // the add's rows carry the same indexes the tab's index will (adopted below)
    const rows = src.rows;
    const members = input.memberIndexes
      ? [...input.memberIndexes].sort((a, b) => a - b)
      : folderMembers(rows);
    const keys = new Set(input.newKeys);
    let bytesAll = 0;
    let bytesNew = 0;
    for (const i of members) {
      const r = rows[i];
      if (!r) continue;
      bytesAll += r.size;
      if (r.hashes && keys.has(fileKey(r.hashes))) bytesNew += r.size;
    }
    const window = effectiveWindow(rows, members);
    const clusterId = `add:${input.daId}`;
    const plan: CreatePlan = {
      instructionId: input.instructionId,
      assetId: input.assetId,
      siteId: input.siteId,
      projectId: input.projectId,
      assetRef: null,
      workflow: null,
      effectiveWorkflow: input.workflow ?? "",
      notes: null,
    };
    const created = {
      dataAcquisitionId: input.daId,
      state: "CREATED",
      createdAt: "",
      externalId: null,
      idempotentReplay: true,
      instructionId: input.instructionId,
      siteId: input.siteId,
      assetId: input.assetId,
      intakeState: null,
      workflow: input.workflow,
    } as DataAcquisitionCreated;
    const phaseTimes = { created: { startedAt: at } };
    const uploadChoice = input.uploads ? setChoiceOf(input.uploadChoice) : null;
    const set: RunnerSet = {
      clusterId,
      ordinal: input.ordinal,
      memberIndexes: members,
      imageCount: members.length,
      sizeBytes: input.uploads ? bytesNew : bytesAll,
      centroid: input.centroid,
      plan,
      turbineLabel: input.turbineLabel,
      instructionLabel: input.instructionLabel,
      uploadChoice,
      phase: "created",
      phaseTimes,
      error: null,
      created,
      boundTo: input.daId,
      uploadKeys: [...input.newKeys],
      ...(input.uploads ? { upload: initialUpload() } : {}),
    };

    let handleKey: string | undefined;
    if (input.handle) {
      const key = `handle:${sid}`;
      handleKey = await remember(key, input.handle)
        .then(() => key)
        .catch(() => undefined);
    }
    const cluster: ClusterIntake = {
      clusterId,
      centroid: input.centroid,
      radiusM: 0,
      imageCount: members.length,
      flightCount: 0,
      firstCapturedAtMs: firstCaptureOf(window),
      dataAcquisitionId: input.daId,
      instructionId: input.instructionId,
      assetId: input.assetId,
      siteId: input.siteId,
      workflow: input.workflow,
      decision: {},
      plan,
      phase: "created",
      phaseTimes,
      error: null,
      uploadChoice,
      ...(set.upload ? { upload: set.upload } : {}),
      turbineLabel: input.turbineLabel,
      instructionLabel: input.instructionLabel,
      sizeBytes: set.sizeBytes,
      boundTo: input.daId,
      uploadKeys: [...input.newKeys],
      ordinal: input.ordinal,
    };
    // DI-3-S16-FE — the add's folder under the field-data root, if it is
    const addRootPath = input.handle
      ? await this.deps.rootPathOf(input.handle).catch(() => null)
      : null;
    const record: SessionRecord = {
      sessionId: sid,
      folderName: src.folderName ?? "Images",
      ...(handleKey ? { handleKey } : {}),
      ...(addRootPath ? { rootPath: addRootPath } : {}),
      fileCount: src.rows.length,
      createdAt: at,
      lastStep: "decide",
      uploadChoice: input.uploadChoice,
      startedAt: at,
      parent: {
        dataAcquisitionId: input.daId,
        sessionId: input.parent?.sessionId ?? null,
        clusterId: input.parent?.clusterId ?? null,
      },
      clusters: [cluster],
    };
    // the record and the active session first: the board re-reads the
    // active session when this tab's runner changes session
    await this.persist(() => saveSession(record));
    await rememberActiveSession(sid);

    // the add's index becomes the tab's index (rows, previews, the upload's
    // bytes) — synchronously with the new state, so no step of the queue ever
    // reads the new rows through the old sets' indexes
    this.disposeClients();
    this.awaitingMembers = false;
    this.index.getState().load({
      sessionId: sid,
      folderName: src.folderName,
      files: src.rows.map((r) => ({
        name: r.name,
        relativePath: r.relativePath,
        size: r.size,
      })),
      skipped: src.skipped,
      aux: src.aux,
    });
    this.index.getState().applyBatch({
      parsed: src.rows.flatMap((r) =>
        r.parsed ? [{ index: r.index, result: r.parsed }] : [],
      ),
      hashed: src.rows.flatMap((r) =>
        r.hashes ? [{ index: r.index, hashes: r.hashes }] : [],
      ),
      errors: src.rows.flatMap((r) =>
        r.error ? [{ index: r.index, message: r.error }] : [],
      ),
    });
    this.index.getState().setPhase("done");
    this.clients = [
      {
        offset: 0,
        count: src.rows.length,
        client: input.add.client,
        phase: "done",
        files: input.add.files,
      },
    ];
    this.uploads.resume();
    this.state.setState({
      ...EMPTY_RUNNER,
      sessionId: sid,
      folderName: src.folderName,
      started: true,
      startedAt: at,
      sets: [set],
      indexStartedAt: at,
    });
    this.pump();
  }

  /** DI-3-F — the bytes of row `index` of this tab's index (null: not read here). */
  fileOf(index: number): Blob | null {
    const slot = this.clients.find(
      (c) => index >= c.offset && index < c.offset + c.count,
    );
    return slot?.files[index - slot.offset] ?? null;
  }

  /** DI-3-F — "Retry the upload" (the verify card) / a stopped upload's Retry. */
  retryUpload(clusterId: string): void {
    this.uploads.retry(clusterId);
  }

  /**
   * DI-3-P7 — "Upload now" on an "after verification" set that has not sent
   * a byte: the choice becomes "now" (persisted) and DI-3-F's driver starts
   * the set's upload as for "now". False when not offered here.
   */
  uploadNow(clusterId: string): boolean {
    return this.uploads.uploadNow(clusterId);
  }

  /** DI-3-P7 — does THIS tab's runner hold the set of `daId` (its actions run here)? */
  holdsAcquisition(daId: string | null | undefined): boolean {
    if (!daId) return false;
    return this.state
      .getState()
      .sets.some((s) => s.created?.dataAcquisitionId === daId);
  }

  /** DI-3-F — "Pause uploads" / "Resume uploads" (the board's banner). */
  pauseUploads(): void {
    this.uploads.pause();
  }

  resumeUploads(): void {
    this.uploads.resume();
  }

  preview(index: number, longEdge?: number): Promise<PreviewAnswer> {
    const slot = this.clients.find(
      (c) => index >= c.offset && index < c.offset + c.count,
    );
    if (!slot) return Promise.resolve({ blob: null });
    return slot.client.preview(index - slot.offset, longEdge);
  }

  private startClient(
    sessionId: string,
    offset: number,
    result: CollectResult,
    knownHashes?: Record<string, HashResult>,
  ): void {
    const slot: ClientSlot = {
      offset,
      count: result.files.length,
      phase: result.files.length > 0 ? "indexing" : "done",
      client: undefined as unknown as IndexClient,
      files: result.files.map((f) => f.file),
    };
    slot.client = new IndexClient({
      store: this.proxyStore(slot),
      ...(this.deps.createWorker
        ? { createWorker: this.deps.createWorker }
        : {}),
      ...(this.deps.flushMs !== undefined
        ? { flushMs: this.deps.flushMs }
        : {}),
    });
    this.clients.push(slot);
    slot.client.start(
      sessionId,
      result.files.map((f) => ({
        file: f.file,
        name: f.file.name,
        size: f.file.size,
        relativePath: f.relativePath,
      })),
      knownHashes ? { knownHashes } : {},
    );
  }

  /**
   * The index store as ONE client sees it: its row indexes shifted by the
   * rows before it, its phase folded into the drop's (done when every
   * folder is done).
   */
  private proxyStore(slot: ClientSlot): StoreApi<IndexState> {
    const real = this.index;
    const shift = <T extends { index?: number }>(
      xs: readonly T[] | undefined,
    ): T[] =>
      (xs ?? []).map((x) =>
        x.index === undefined ? x : { ...x, index: x.index + slot.offset },
      );
    const getState = (): IndexState => ({
      ...real.getState(),
      applyBatch: (b) => {
        real.getState().applyBatch({
          parsed: shift(b.parsed),
          hashed: shift(b.hashed),
          errors: shift(b.errors),
        });
      },
      setPhase: (p) => {
        slot.phase = p;
        if (p === "cancelled" || p === "error") {
          real.getState().setPhase(p);
          return;
        }
        if (this.clients.every((c) => c.phase === "done"))
          real.getState().setPhase("done");
      },
    });
    return { ...real, getState };
  }

  private disposeClients(): void {
    for (const c of this.clients) c.client.dispose();
    this.clients = [];
  }

  // ── the pipeline ──────────────────────────────────────────────────────

  /** Start: every set of the drop, in row order. */
  async start(input: StartInput): Promise<void> {
    const now = this.deps.now();
    const prior = await getSession(input.sessionId);
    const priorClusters = new Map(
      (prior?.clusters ?? []).map((c) => [c.clusterId, c] as const),
    );
    const sets: RunnerSet[] = input.sets.map(
      ({ set, plan, uploads, imageUpload, turbineLabel, instructionLabel }) => {
        const p = priorClusters.get(set.clusterId);
        return {
          clusterId: set.clusterId,
          ordinal: set.ordinal,
          memberIndexes: set.memberIndexes.slice(),
          imageCount: set.imageCount,
          sizeBytes: set.sizeBytes,
          centroid: set.centroid,
          plan,
          turbineLabel,
          instructionLabel,
          // M5-NODE-01 — "don't upload" is the DROP's choice; a set that
          // uploads under it (an `automatic` version beside an `optional`
          // one) goes "now", as DI-D43's default
          uploadChoice: uploads ? setChoiceOf(input.uploadChoice) : null,
          ...(imageUpload ? { imageUpload } : {}),
          phase: "decided",
          phaseTimes: { decided: { startedAt: now.toISOString() } },
          error: null,
          ...(p?.metadataComplete
            ? { metadataComplete: p.metadataComplete }
            : {}),
          // DI-3-F — a re-drop of the same session keeps its ledger
          ...(uploads
            ? { upload: p?.upload ?? initialUpload() }
            : p?.withhold
              ? { withhold: p.withhold }
              : {}),
        };
      },
    );
    this.state.setState({
      sessionId: input.sessionId,
      folderName: input.folderName,
      started: true,
      startedAt: now.toISOString(),
      sets,
    });
    const labels = new Map(
      input.sets.map((s) => [s.set.clusterId, s] as const),
    );
    const clusters: ClusterIntake[] = input.sets.map(
      ({ set, decision, plan }, k) => {
        const p = priorClusters.get(set.clusterId);
        const r = sets[k];
        return {
          ...(p ?? {}),
          clusterId: set.clusterId,
          centroid: set.centroid,
          radiusM: set.radiusM,
          imageCount: set.imageCount,
          flightCount: set.flightCount,
          firstCapturedAtMs: firstCaptureOf(set.window),
          decision,
          plan,
          // M3C-04 — the tenant the set lives in (NULL = own)
          targetTenantSlug: plan.targetTenantSlug ?? null,
          targetTenantName: plan.targetTenantName ?? null,
          phase: "decided",
          phaseTimes: r?.phaseTimes ?? {},
          error: null,
          uploadChoice: r?.uploadChoice ?? null,
          ...(r?.imageUpload ? { imageUpload: r.imageUpload } : {}),
          ...(r?.upload ? { upload: r.upload } : {}),
          turbineLabel: labels.get(set.clusterId)?.turbineLabel ?? "",
          instructionLabel: labels.get(set.clusterId)?.instructionLabel ?? "",
          sizeBytes: set.sizeBytes,
        };
      },
    );
    const others = (prior?.clusters ?? []).filter(
      (c) => !input.sets.some((s) => s.set.clusterId === c.clusterId),
    );
    // DI-3-P6 — a Start under an ended session's id (a re-drop) makes it
    // live again: the prior record's `endedAt` does not carry over
    const priorLive: Partial<SessionRecord> = { ...(prior ?? {}) };
    delete priorLive.endedAt;
    // DI-3-S16-FE — the browser named no folder (loose files): the record
    // keeps the placeholder name and says so (never looked for under the root)
    const loose =
      input.folderName === null &&
      (!prior?.folderName || prior.looseDrop === true);
    delete priorLive.looseDrop;
    // DI-3-S16-FE — at drop time, where the dropped folder sits under the
    // granted root (`root.resolve(dropHandle)`), only when it answers
    const dropHandle = prior?.handleKey
      ? await recall<unknown>(prior.handleKey)
          .then((r) => r?.handle ?? null)
          .catch(() => null)
      : null;
    const rootPath = dropHandle
      ? await this.deps.rootPathOf(dropHandle).catch(() => null)
      : null;
    const record: SessionRecord = {
      ...priorLive,
      ...(rootPath ? { rootPath } : {}),
      ...(loose ? { looseDrop: true } : {}),
      sessionId: input.sessionId,
      folderName: input.folderName ?? prior?.folderName ?? LOOSE_DROP_NAME,
      fileCount: this.index.getState().total || (prior?.fileCount ?? 0),
      createdAt: prior?.createdAt ?? now.toISOString(),
      lastStep: "decide",
      intent: input.intent,
      uploadChoice: input.uploadChoice,
      startedAt: now.toISOString(),
      clusters: [...clusters, ...others],
    };
    await this.persist(() => saveSession(record));
    await rememberActiveSession(input.sessionId);
    this.pump();
  }

  /**
   * Retry the step a set stopped on. DI-3-S68 B2 — by hand: the ladder
   * starts over. B4 — a set stopped on unreadable files first reads those
   * files again (`rereadUnreadable`), then the round trip looks once more.
   */
  retry(clusterId: string): void {
    const s = this.state.getState().sets.find((x) => x.clusterId === clusterId);
    if (s?.error?.reason === UNREADABLE_REASON) {
      // DI-3-S68 F6 — single-flight; "Reading again… n / m" while it reads
      if (s.reread) return;
      this.disarmProbe(clusterId);
      void this.rereadUnreadable(s).then((read) => {
        patchSet(this.state, clusterId, { reread: null });
        const cur = this.state
          .getState()
          .sets.find((x) => x.clusterId === clusterId);
        const left = cur ? this.unhashedOf(cur) : 0;
        if (read === 0 && left > 0 && cur?.error) {
          // DI-3-S68 F6 — not one file reads: the drive is gone. Say so
          // (never the same notice coming back unchanged) and wait for it
          const error = unreadableError(left, "gone");
          patchSet(this.state, clusterId, { error });
          const sid = this.state.getState().sessionId;
          if (sid)
            void this.persist(() => updateCluster(sid, clusterId, { error }));
          this.armProbe(clusterId);
          return;
        }
        this.clearError(clusterId);
      });
      return;
    }
    this.clearError(clusterId);
  }

  /**
   * ZV-MIS-01C — the create was refused 409 `instruction_not_available` (the
   * mission was filled meanwhile / is no longer open here): the pilot chose
   * again from the refreshed drop targets. Only that one plan swap — another
   * mission, or the unplanned drop (a placeholder) — and only for a set
   * stopped on that refusal; then the create runs again.
   */
  rechooseMission(
    clusterId: string,
    patch: Pick<CreatePlan, "instructionId" | "assetRef" | "dropPosition">,
  ): boolean {
    const s = this.state.getState().sets.find((x) => x.clusterId === clusterId);
    if (
      s?.phase !== "decided" ||
      s.error?.reason !== "instruction_not_available"
    )
      return false;
    const plan: CreatePlan = {
      ...s.plan,
      instructionId: patch.instructionId,
      assetRef: patch.assetRef,
      dropPosition: patch.dropPosition ?? null,
      assetId: null,
      siteId: null,
      projectId: null,
    };
    patchSet(this.state, clusterId, { plan });
    const sid = this.state.getState().sessionId;
    if (sid) void this.persist(() => updateCluster(sid, clusterId, { plan }));
    this.clearError(clusterId);
    return true;
  }

  /** DI-3-S68 F6 — members of `set` without hashes in this tab's index. */
  private unhashedOf(set: RunnerSet): number {
    const rows = this.index.getState().rows;
    let n = 0;
    for (const i of set.memberIndexes) if (!rows[i]?.hashes) n++;
    return n;
  }

  /**
   * DI-3-S68 F6 — "Choose the folder…" in the tab that runs the drop: the
   * files of the picked folder take the place of the ones this tab holds
   * (by file name + size — `rowHashKey`, the key the resume uses), and
   * every set stopped on unreadable files reads them again. Returns how
   * many files were taken (0: the folder does not hold this drop's files —
   * nothing changed).
   */
  refreshFiles(result: CollectResult): number {
    const byKey = new Map<string, Blob>();
    for (const f of result.files)
      byKey.set(
        rowHashKey({ relativePath: f.relativePath, size: f.file.size }),
        f.file,
      );
    const rows = this.index.getState().rows;
    let taken = 0;
    for (const slot of this.clients) {
      slot.files = slot.files.map((old, k) => {
        const r = rows[slot.offset + k];
        const fresh = r
          ? byKey.get(
              rowHashKey({ relativePath: r.relativePath, size: r.size }),
            )
          : undefined;
        if (!fresh) return old;
        taken++;
        return fresh;
      });
    }
    if (taken === 0) return 0;
    for (const s of this.state.getState().sets)
      if (s.error?.reason === UNREADABLE_REASON) this.retry(s.clusterId);
    return taken;
  }

  private readonly probeTimers = new Map<string, unknown>();

  /** DI-3-S68 F6b — look again in `DRIVE_PROBE_MS` whether the drive is back. */
  private armProbe(clusterId: string): void {
    this.disarmProbe(clusterId);
    this.probeTimers.set(
      clusterId,
      this.retryClock.setTimeout(() => {
        this.probeTimers.delete(clusterId);
        void this.probe(clusterId);
      }, DRIVE_PROBE_MS),
    );
  }

  private disarmProbe(clusterId: string): void {
    const id = this.probeTimers.get(clusterId);
    if (id === undefined) return;
    this.retryClock.clearTimeout(id);
    this.probeTimers.delete(clusterId);
  }

  /** DI-3-S68 F6b — one byte of the first unread member: readable → read the set again. */
  private async probe(clusterId: string): Promise<void> {
    const s = this.state.getState().sets.find((x) => x.clusterId === clusterId);
    if (s?.error?.reason !== UNREADABLE_REASON || s.reread) return;
    const rows = this.index.getState().rows;
    const i = s.memberIndexes.find((k) => rows[k] && !rows[k].hashes);
    const file = i === undefined ? null : this.fileOf(i);
    if (!file) return; // this tab holds no file of it: "Choose the folder…"
    if (!this.retryGate()) {
      this.armProbe(clusterId);
      return;
    }
    const readable = await this.deps.readable(file).catch(() => false);
    if (readable) this.retry(clusterId);
    else this.armProbe(clusterId);
  }

  /**
   * DI-3-S68 B4 — read and hash again every member of `set` without a hash,
   * from the files this tab holds (the drive reconnected). A file that still
   * cannot be read stays without one — the next round trip stops again with
   * the new count. Resolves to how many were read.
   */
  async rereadUnreadable(set: RunnerSet): Promise<number> {
    const rows = this.index.getState().rows;
    const hashed: { index: number; hashes: HashResult }[] = [];
    const todo = set.memberIndexes.filter((i) => rows[i] && !rows[i].hashes);
    // DI-3-S68 F6 — visible: "Reading again… n / m"
    patchSet(this.state, set.clusterId, {
      reread: { done: 0, total: todo.length },
    });
    let done = 0;
    for (const i of todo) {
      const file = this.fileOf(i);
      if (file) {
        try {
          hashed.push({ index: i, hashes: await this.deps.hash(file) });
        } catch {
          /* still unreadable: counted again by the round trip */
        }
      }
      done++;
      patchSet(this.state, set.clusterId, {
        reread: { done, total: todo.length },
      });
    }
    if (hashed.length > 0) this.index.getState().applyBatch({ hashed });
    return hashed.length;
  }

  private clearError(clusterId: string): void {
    this.disarm(clusterId);
    this.disarmProbe(clusterId);
    patchSet(this.state, clusterId, { error: null, autoRetry: null });
    const sid = this.state.getState().sessionId;
    if (sid)
      void this.persist(() =>
        updateCluster(sid, clusterId, { error: null, autoRetry: null }),
      );
    this.pump();
  }

  /**
   * DI-3-S68 B2 — only the tab holding the runner claim retries by itself
   * (`resume.ts` wires `claim.role === "owner"`); without a claim (a test, a
   * browser without IndexedDB) the tab runs alone and may.
   */
  setAutoRetryGate(gate: () => boolean): void {
    this.retryGate = gate;
  }

  /** DI-3-S68 B2 — arm the ladder's timer of every set that waits on it. */
  private armAll(): void {
    for (const s of this.state.getState().sets) this.arm(s.clusterId);
  }

  private arm(clusterId: string, delayMs?: number): void {
    this.disarm(clusterId);
    const s = this.state.getState().sets.find((x) => x.clusterId === clusterId);
    const at = s?.error ? s.autoRetry?.nextAt : null;
    if (!at) return;
    const ms =
      delayMs ?? Math.max(0, Date.parse(at) - this.deps.now().getTime());
    this.retryTimers.set(
      clusterId,
      this.retryClock.setTimeout(() => {
        this.retryTimers.delete(clusterId);
        this.fireOne(clusterId, "timer");
      }, ms),
    );
  }

  private disarm(clusterId: string): void {
    const id = this.retryTimers.get(clusterId);
    if (id === undefined) return;
    this.retryClock.clearTimeout(id);
    this.retryTimers.delete(clusterId);
  }

  private disarmAll(): void {
    for (const id of this.retryTimers.values())
      this.retryClock.clearTimeout(id);
    this.retryTimers.clear();
  }

  /** DI-3-S68 B2 — `online`: every set on the ladder tries now. */
  private fireDue(trigger: "timer" | "online"): void {
    for (const s of this.state.getState().sets)
      this.fireOne(s.clusterId, trigger);
  }

  /**
   * DI-3-S68 B2 — one automatic attempt: the gate (the claiming tab), the set
   * due and not in flight (single-flight); the counter is persisted BEFORE
   * the step runs, so a reload mid-attempt continues the ladder, never
   * restarts it.
   */
  private fireOne(clusterId: string, trigger: "timer" | "online"): void {
    const s = this.state.getState().sets.find((x) => x.clusterId === clusterId);
    if (!s?.autoRetry || !retryDue(s, this.deps.now(), trigger)) return;
    if (!this.retryGate()) {
      // not the claiming tab (yet): look again, never consume an attempt
      this.arm(clusterId, RETRY_GATE_RECHECK_MS);
      return;
    }
    this.disarm(clusterId);
    const autoRetry = ladderOnAttempt(s.autoRetry);
    patchSet(this.state, clusterId, { error: null, autoRetry });
    const sid = this.state.getState().sessionId;
    if (sid)
      void this.persist(() =>
        updateCluster(sid, clusterId, { error: null, autoRetry }),
      );
    this.pump();
  }

  /**
   * After a reload: show the session's started sets and continue what needs
   * no files (a create, `metadata-complete`). A set waiting for its round
   * trip waits for the folder ("Continue session").
   */
  restore(session: SessionRecord): void {
    if (
      this.state.getState().sessionId === session.sessionId &&
      this.state.getState().started
    )
      return;
    const sets: RunnerSet[] = (session.clusters ?? []).flatMap((c, k) => {
      const r = runnerSetFromCluster(c, k);
      return r ? [r] : [];
    });
    if (sets.length === 0) return;
    this.state.setState({
      sessionId: session.sessionId,
      folderName: session.folderName,
      started: true,
      startedAt: session.startedAt ?? null,
      sets,
    });
    // DI-3-S68 B2 — the persisted ladder goes on where it was
    this.armAll();
    this.pump();
  }

  /**
   * DI-3-D — resume: read the restored session's folder again (the resume
   * module re-acquired the handle). The started sets keep their phases; once
   * the heads are parsed each gets its members back by its stable id, and the
   * queue continues (a round trip replays; `metadata-complete` is posted once).
   *
   * DI-3-P2 — the persisted hashes go INTO the worker (`knownHashes`): a file
   * whose file name + size matches is head-parsed only and reports them;
   * any other file is hashed as on a first drop. Returns (and puts on the
   * runner state for the board's resume line) how many files were read and
   * how many hashes were kept.
   */
  reattach(session: SessionRecord, result: CollectResult): ReattachCounts {
    const sid = session.sessionId;
    if (this.state.getState().sessionId !== sid) this.restore(session);
    this.disposeClients();
    // DI-3-S15-FE — keyed by file name + size (a loose-file drop and a
    // picked folder read the same file under different paths); a pre-S15
    // record's path keys are read through their base name
    const known = knownForFolder(
      knownHashesOf((session.clusters ?? []).map((c) => c.rowHashes)),
      result.files.map((f) => ({
        relativePath: f.relativePath,
        size: f.file.size,
      })),
    );
    let hashesKept = 0;
    for (const f of result.files)
      if (
        known[rowHashKey({ relativePath: f.relativePath, size: f.file.size })]
      )
        hashesKept++;
    const reattached: ReattachCounts = {
      files: result.files.length,
      hashesKept,
    };
    this.awaitingMembers = true;
    // DI-3-S68 F6 — the folder is read again: a set stopped on files it
    // could not read starts over on the fresh files (the round trip counts
    // what is still unreadable and stops again if any is)
    for (const s of this.state.getState().sets)
      if (s.error?.reason === UNREADABLE_REASON) {
        this.disarmProbe(s.clusterId);
        patchSet(this.state, s.clusterId, { error: null, autoRetry: null });
        void this.persist(() =>
          updateCluster(sid, s.clusterId, { error: null, autoRetry: null }),
        );
      }
    this.state.setState({
      indexStartedAt: this.deps.now().toISOString(),
      reattached,
    });
    this.index.getState().load({
      sessionId: sid,
      folderName: result.folderName,
      files: result.files.map((f) => ({
        name: f.file.name,
        relativePath: f.relativePath,
        size: f.file.size,
      })),
      skipped: result.skipped,
      aux: auxOf(result),
    });
    this.startClient(sid, 0, result, known);
    return reattached;
  }

  /** DI-3-D — after a reattach's parse: every started set's members, by its stable id. */
  private giveMembersBack(): void {
    this.awaitingMembers = false;
    const rows = this.index.getState().rows;
    const byId = new Map(
      proposalSets(groupForDecide(rows), rows).map(
        (p) => [p.clusterId, p] as const,
      ),
    );
    const sid = this.state.getState().sessionId;
    for (const s of this.state.getState().sets) {
      if (s.memberIndexes.length > 0) continue;
      if (s.boundTo) {
        // DI-3-G — an add owns the whole folder (its own session)
        const members = folderMembers(rows);
        if (members.length > 0) {
          const keys = s.uploadKeys ? new Set(s.uploadKeys) : null;
          let bytes = 0;
          for (const i of members) {
            const r = rows[i];
            if (r && (!keys || (r.hashes && keys.has(fileKey(r.hashes)))))
              bytes += r.size;
          }
          patchSet(this.state, s.clusterId, {
            memberIndexes: members,
            sizeBytes: bytes,
          });
          continue;
        }
      }
      const p = byId.get(s.clusterId);
      if (p) {
        patchSet(this.state, s.clusterId, {
          memberIndexes: p.memberIndexes.slice(),
          sizeBytes: p.sizeBytes,
        });
        continue;
      }
      if (s.phase !== "created" && s.phase !== "syncing") continue;
      const error: SetError = {
        step: "sync",
        message: `This folder does not hold data set ${String(s.ordinal)} — open the card folder of this session again on Upload.`,
        status: null,
        reason: "set_not_in_folder",
      };
      patchSet(this.state, s.clusterId, { error });
      if (sid)
        void this.persist(() => updateCluster(sid, s.clusterId, { error }));
    }
    this.pump();
  }

  /**
   * Start over: the worker, the rows, the sets — gone; the browser forgets the
   * session. DI-3-S40 R2 — first, every set this drop CREATED on the server
   * that has not reached `running` is withdrawn ("start over"): the browser
   * says it abandons it instead of leaving an empty set behind. A set bound
   * to an existing acquisition (DI-3-G "Add images…") is never withdrawn — it
   * is the pilot's existing set. A failed withdraw does not block the clear;
   * the server's stale-set sweep (R1) retires what is left.
   */
  startOver(): void {
    for (const s of this.state.getState().sets) {
      const daId = s.created?.dataAcquisitionId;
      if (!daId || s.boundTo || s.phase === "running") continue;
      void this.deps.withdraw(daId, START_OVER_REASON).catch(() => undefined);
    }
    this.clearSession();
    void forgetActiveSession();
  }

  /** DI-3-S40 (B′) — the files this drop holds (every folder read), or null. */
  heldFiles(): CollectResult | null {
    if (this.dropped.length === 0) return null;
    const files = this.dropped.flatMap((r) => r.files);
    if (files.length === 0) return null;
    const aux = this.dropped.flatMap((r) => r.aux ?? []);
    return {
      files,
      ...(aux.length > 0 ? { aux } : {}),
      skipped: this.dropped.reduce((n, r) => n + r.skipped, 0),
      folderName: this.dropped[0]?.folderName ?? null,
    };
  }

  /** DI-3-S40 (B′) — every record write queued so far has landed. */
  async settled(): Promise<void> {
    await this.persistChain;
  }

  /**
   * DI-3-P6 — the session ENDED by itself (`sessionEnd.ts:sessionDone`: every
   * started set left the intake, nothing pending in this tab): the record is
   * KEPT with `endedAt` (written first, so a board that re-reads it in
   * between never draws the ended drop), the browser forgets it as the
   * active session, then the worker, the rows and the runner state go — as
   * "Start over" does. Resolves to the ended session's id, or null when there
   * was nothing to release (or it is already being released).
   */
  async release(): Promise<string | null> {
    const s = this.state.getState();
    const sid = s.sessionId;
    if (!sid || !s.started || this.releasing === sid) return null;
    this.releasing = sid;
    try {
      const at = this.deps.now().toISOString();
      await this.persist(() =>
        updateSession(sid, (rec) => ({ ...rec, endedAt: at })),
      );
      // a new drop may have replaced the session meanwhile: forget only ours
      if ((await activeSessionId()) === sid) await forgetActiveSession();
      if (this.state.getState().sessionId === sid) this.clearSession();
      return sid;
    } finally {
      this.releasing = null;
    }
  }

  /** The worker, the rows, the runner state — gone (Start over, release). */
  private clearSession(): void {
    this.disarmAll();
    this.disposeClients();
    this.awaitingMembers = false;
    this.index.getState().reset();
    this.state.setState({ ...EMPTY_RUNNER });
    syncProgressStore.getState().reset();
    this.uploads.resume();
    this.dropped = [];
  }

  /** Tests: stop listening (the app singleton lives for the tab). */
  destroy(): void {
    this.disarmAll();
    for (const id of this.probeTimers.values())
      this.retryClock.clearTimeout(id);
    this.probeTimers.clear();
    this.win?.removeEventListener("online", this.onOnline);
    this.disposeClients();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.uploads.dispose();
    this.offApproved?.();
    this.offApproved = null;
  }

  // ── the queue ─────────────────────────────────────────────────────────

  private pump(): void {
    if (!this.state.getState().started) return;
    this.uploads.pump();
    if (this.running) {
      this.dirty = true;
      return;
    }
    this.running = true;
    this.state.setState({ busy: true });
    void (async () => {
      try {
        do {
          while (await this.step()) {
            /* one request at a time */
          }
        } while (this.takeDirty());
      } finally {
        this.running = false;
        this.state.setState({ busy: false });
      }
    })();
  }

  /** A trigger arrived while the queue ran: look once more. */
  private takeDirty(): boolean {
    const d = this.dirty;
    this.dirty = false;
    return d;
  }

  /** Does ONE step of the first set that can move; false when none can. */
  private async step(): Promise<boolean> {
    const sets = this.state.getState().sets;
    // 1. every create first, in row order
    const toCreate = sets.find(
      (s) =>
        s.error === null && (s.phase === "decided" || s.phase === "creating"),
    );
    if (toCreate) {
      await this.doCreate(toCreate);
      return true;
    }
    // 2. a created set whose rows are hashed
    const indexDone = INDEX_DONE.includes(this.index.getState().phase);
    const rows = this.index.getState().rows;
    const toSync = sets.find(
      (s) =>
        s.error === null &&
        (s.phase === "created" || s.phase === "syncing") &&
        s.memberIndexes.length > 0 &&
        sendPlan(rows, s.memberIndexes, indexDone).ready,
    );
    if (toSync) {
      await this.doSync(toSync);
      return true;
    }
    // 3. metadata-complete
    const toComplete = sets.find(
      (s) =>
        s.error === null && (s.phase === "synced" || s.phase === "completing"),
    );
    if (toComplete) {
      await this.doComplete(toComplete);
      return true;
    }
    return false;
  }

  private async transition(
    set: RunnerSet,
    to: SetPhase,
    extra: Partial<RunnerSet> = {},
    persisted: Partial<ClusterIntake> = {},
  ): Promise<RunnerSet> {
    const cur =
      this.state.getState().sets.find((s) => s.clusterId === set.clusterId) ??
      set;
    const phaseTimes = enter(cur.phaseTimes, cur.phase, to, this.deps.now());
    patchSet(this.state, set.clusterId, { ...extra, phase: to, phaseTimes });
    // DI-3-F — `synced` / `running` may let an upload or a withhold start
    this.uploads.pump();
    const sid = this.state.getState().sessionId;
    if (sid) {
      await this.persist(() =>
        updateCluster(sid, set.clusterId, {
          ...persisted,
          phase: to,
          phaseTimes,
          ...(extra.error !== undefined ? { error: extra.error } : {}),
        }),
      );
    }
    return { ...cur, ...extra, phase: to, phaseTimes };
  }

  /**
   * A step failed: the set goes back with its error. DI-3-S68 B2 — a
   * transport failure puts it on the ladder (the next rung, persisted with
   * the error); any other failure waits for Retry.
   */
  private async fail(
    set: RunnerSet,
    back: SetPhase,
    error: SetError,
  ): Promise<void> {
    const cur =
      this.state.getState().sets.find((s) => s.clusterId === set.clusterId) ??
      set;
    const autoRetry: AutoRetry | null = ladderAfterFailure(
      cur.autoRetry,
      error,
      this.deps.now(),
    );
    await this.transition(
      set,
      back,
      { error, autoRetry },
      { error, autoRetry },
    );
    this.arm(set.clusterId);
  }

  private async doCreate(set: RunnerSet): Promise<void> {
    const sid = this.state.getState().sessionId;
    if (!sid) return;
    const cur = await this.transition(set, "creating", { error: null });
    const p = cur.plan;
    try {
      const created = await this.deps.create({
        sessionId: sid,
        clusterId: set.clusterId,
        instructionId: p.instructionId,
        assetId: p.assetId,
        siteId: p.siteId,
        notes: p.notes,
        workflow: p.workflow,
        projectId: p.projectId,
        assetRef: p.assetRef,
        // M3C-04 — the version, and the owner's tenant of a shared node
        workflowVersionId: p.workflowVersionId ?? null,
        targetTenantSlug: p.targetTenantSlug ?? null,
        targetTenantName: p.targetTenantName ?? null,
        // ZV-MIS-01C — a mission's create carries the set's centroid
        ...(p.dropPosition ? { dropPosition: p.dropPosition } : {}),
      });
      await this.transition(
        cur,
        "created",
        { created, autoRetry: null },
        {
          autoRetry: null,
          dataAcquisitionId: created.dataAcquisitionId,
          instructionId: created.instructionId ?? null,
          assetId: created.assetId ?? null,
          siteId: created.siteId ?? null,
          intakeState: created.intakeState ?? null,
          workflow: created.workflow ?? null,
          targetTenantSlug: p.targetTenantSlug ?? null,
          targetTenantName: p.targetTenantName ?? null,
          ...(created.externalId ? { externalId: created.externalId } : {}),
        },
      );
    } catch (e) {
      await this.fail(
        cur,
        "decided",
        errorOf("create", e, p.instructionId !== null),
      );
    }
  }

  private async doSync(set: RunnerSet): Promise<void> {
    const daId = set.created?.dataAcquisitionId;
    if (!daId) return;
    const st = this.index.getState();
    const plan = sendPlan(
      st.rows,
      set.memberIndexes,
      INDEX_DONE.includes(st.phase),
    );
    // DI-3-S68 B4 — files that could not be read stop the set BEFORE its
    // rows: never sent silently incomplete (Retry reads them again)
    if (plan.unhashed > 0) {
      patchSet(this.state, set.clusterId, {
        sendStats: { duplicates: plan.duplicates, unhashed: plan.unhashed },
      });
      await this.fail(set, "created", unreadableError(plan.unhashed));
      // DI-3-S68 F6b — the drive may come back by itself: look for it
      this.armProbe(set.clusterId);
      return;
    }
    // DI-3-D — the members' hashes, so a resume need not wait for a re-hash
    // (DI-3-S15-FE: keyed by file name + size; a same-name, same-size pair
    // of different content carries no key — a re-read hashes both)
    const rowHashes = hashRecordOf(plan.send);
    const cur = await this.transition(
      set,
      "syncing",
      {
        sendStats: { duplicates: plan.duplicates, unhashed: plan.unhashed },
        syncProgress: { sent: 0, total: plan.send.length },
      },
      { rowHashes, syncProgress: { sent: 0, total: plan.send.length } },
    );
    try {
      const roundTrip = await syncAcquisition({
        dataAcquisitionId: daId,
        rows: plan.send,
        post: this.deps.post,
        onProgress: (sent, total) => {
          syncProgressStore.getState().set({ active: true, sent, total });
          patchSet(this.state, set.clusterId, {
            syncProgress: { sent, total },
          });
          const sid = this.state.getState().sessionId;
          if (sid && sent > 0)
            void this.persist(() =>
              updateCluster(sid, set.clusterId, {
                syncProgress: { sent, total },
              }),
            );
        },
      });
      await this.transition(
        cur,
        "synced",
        { roundTrip, autoRetry: null },
        { roundTrip, autoRetry: null },
      );
    } catch (e) {
      await this.fail(cur, "created", errorOf("sync", e));
    } finally {
      syncProgressStore.getState().reset();
    }
  }

  private async doComplete(set: RunnerSet): Promise<void> {
    const daId = set.created?.dataAcquisitionId;
    if (!daId) return;
    // ONCE per set per session: a repeated Start of the same session posts it
    // again only when its round trip created new points.
    if (set.metadataComplete && (set.roundTrip?.created ?? 0) === 0) {
      await this.transition(set, "running");
      return;
    }
    const cur = await this.transition(set, "completing");
    // DI-3-S75 (arrival order, option b) — the folder's `*_pos.txt` first, in
    // a session of its own whose ingest has finished before metadata-complete
    // triggers the first geometry run; bounded, best effort, never an error
    await this.sendPosFiles(cur, daId);
    try {
      const r = await this.deps.complete(daId);
      const metadataComplete = {
        outcome: r.outcome,
        intakeState: r.intakeState,
        jobId: r.jobId,
        at: this.deps.now().toISOString(),
      };
      // DI-3-G — an add the server calls `unchanged` (no new input: every
      // file was in the set after all): its upload ends without a PUT
      const skipUpload =
        Boolean(set.boundTo) &&
        r.outcome === "unchanged" &&
        cur.upload !== undefined &&
        !uploadFinished(cur.upload);
      const upload = skipUpload
        ? {
            ...(cur.upload ?? initialUpload()),
            phase: "present" as const,
            error: null,
            retryAt: null,
            finishedAt: this.deps.now().toISOString(),
          }
        : null;
      await this.transition(
        cur,
        "running",
        { metadataComplete, autoRetry: null, ...(upload ? { upload } : {}) },
        {
          autoRetry: null,
          metadataComplete,
          intakeState: r.intakeState,
          ...(upload ? { upload } : {}),
        },
      );
    } catch (e) {
      await this.fail(cur, "synced", errorOf("complete", e));
    }
  }

  /**
   * DI-3-S75 — the set's `*_pos.txt` (its member images' folders) in a
   * pos-only upload session, waited for at most `POS_SESSION_TIMEOUT_MS`.
   * Every set, a withholding workflow's too: an `aux` session is no image
   * upload (inspection-3/v1 2.7.0 — the workflow's image-upload mode does
   * not apply; the images stay on the PC). Not remembered: a reload during
   * `completing` runs `doComplete` again and so sends it again (the ingest
   * dedupes on the path) — the simpler of redo / skip.
   */
  private async sendPosFiles(set: RunnerSet, daId: string): Promise<void> {
    const files = auxFilesOf(
      this.index.getState().rows,
      set.memberIndexes,
      this.index.getState().aux,
    );
    if (files.length === 0) return;
    const up = this.posDeps.upload;
    await sendPosSession({
      daId,
      files,
      api: up.api ?? REAL_UPLOAD_API,
      fetch: up.fetch,
      sleep: up.sleep,
      now: () => this.deps.now().getTime(),
      pollMs: up.pollMs,
      timeoutMs: this.posDeps.posTimeoutMs,
    });
  }

  /** Session writes are read-modify-write: one at a time, in order. */
  private persist<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.persistChain.then(fn, fn);
    this.persistChain = next.catch(() => undefined);
    return next;
  }
}

/** The tab's runner — created with the module, alive for the SPA tab. */
export const intakeRunner = new IntakeRunner();
